import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  addResort,
  updateStudyDescription,
  deleteResort,
  addReport,
  editResort,
} from "../../api/studyApi";
import AddItemsModal from "../template/addItemsModal";
import AddItemModal from "../template/addItemModal";
import MagnifyingGlass from "../../assets/icons/MagnifyingGlass";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { FormTemplate, Report, ReportState, Resort, TemplateCategory } from "../../types/Rows";
import { createCentralMonitoringReport, getCentralMonitoringReports } from "../../api/centralMonitoringApi";
import EditIcon from "../../components/icons/EditIcon";
import ReportDropdown from "../../components/ReportDropdown";
import DeleteIcon from "../../components/icons/DeleteIcon";
import { cn } from "../../lib/utils";
import { byVisitNumberDesc } from "../../utils/reportOrder";
import { formatDateTime } from "../../components/lists/formatDate";
import { fetchStudyIssueSummary, type ResortIssueSummary } from "../../api/siteIssuesApi";
import { ResortRegisterButton } from "../siteIssues/ResortRegisterButton";
import { ResortIssuesPreview } from "../siteIssues/ResortIssuesPreview";

const STATUS_CONFIG: Record<number, { label: string; pill: string; accent: string }> = {
  [ReportState.INPROGRESS]: { label: "W trakcie",       pill: "bg-[#FEF3E2] text-[#C07810]",  accent: "#F4A832" },
  [ReportState.TO_VERIFY]:  { label: "Do weryfikacji",  pill: "bg-[#EDF3FC] text-[#3570C4]",  accent: "#5B8FD8" },
  [ReportState.TO_CORRECT]: { label: "Do poprawy",      pill: "bg-[#FEE8E8] text-[#C03030]",  accent: "#E05050" },
  [ReportState.ACCEPTED]:   { label: "Zaakceptowano",   pill: "bg-[#E6F7F1] text-[#1A8055]",  accent: "#2BAE72" },
};

const STATUS_FILTERS = [
  { label: "Wszystkie", value: null },
  { label: "W trakcie",       value: ReportState.INPROGRESS },
  { label: "Do weryfikacji",  value: ReportState.TO_VERIFY },
  { label: "Do poprawy",      value: ReportState.TO_CORRECT },
  { label: "Zaakceptowane",   value: ReportState.ACCEPTED },
];

const Reports = ({ data, templates, studyId }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchPhrase, setSearchPhrase] = useState<string>("");
  const [activeStatus, setActiveStatus] = useState<ReportState | null>(null);
  const [activeResortId, setActiveResortId] = useState<string | null>(null);
  const resortRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const onSearchChange = ({ target }: React.ChangeEvent<HTMLInputElement>) =>
    setSearchPhrase(target.value.toLocaleLowerCase());

  const { mutate: addResortMutation } = useMutation(
    async ({ name, address }: { name: string; address: string }) => {
      return addResort(data.id, name, address);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["Studies", data.id]);
      },
    }
  );

  const { mutate: editResortMutation } = useMutation(
    async ({
      resortId,
      name,
      address,
    }: {
      resortId: string;
      name: string;
      address: string;
    }) => {
      return editResort(data.id, resortId, name, address);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["Studies", data.id]);
      },
    }
  );

  const { mutate: updateDescription } = useMutation(
    async (description: string) => {
      return updateStudyDescription(data.id, description);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["Studies", data.id]);
      },
    }
  );

  const { mutate: resortDelete } = useMutation(
    async (resortId: string) => {
      return deleteResort(data.id, resortId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["Studies", data.id]);
      },
    }
  );

  const { mutate: addReportMutation } = useMutation(
    async ({
      resortId,
      templateId,
    }: {
      resortId: string;
      templateId: string;
    }) => {
      await addReport(data.id, resortId, templateId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["Studies", data.id]);
      },
    }
  );

  const centralMonitoringTemplate = (templates ?? []).find(
    (x: FormTemplate) => x.templateType === TemplateCategory.CENTRAL_MONITORING && x.studyId === studyId
  );

  const { mutate: createCMReport, isLoading: isCreatingCMReport } = useMutation(
    async () => createCentralMonitoringReport(studyId),
    {
      onSuccess: (reportData) => {
        navigate(`/reports/${reportData.id}`);
      },
    }
  );

  const { data: cmReports = [] } = useQuery(
    ["CentralMonitoringReports", studyId],
    () => getCentralMonitoringReports(studyId),
    { enabled: !!studyId }
  );

  // Rejestr ośrodka: liczniki dla ośrodków badania (bez endpointu na serwerze przyciski działają bez liczb)
  const { data: issueSummary, isError: issueSummaryFailed } = useQuery(["siteIssuesSummary", studyId], () => fetchStudyIssueSummary(studyId), {
    enabled: !!studyId, retry: false,
  });
  const summaryFor = (resortId: string): ResortIssueSummary | undefined =>
    issueSummary ? issueSummary.find((s) => s.resortId === resortId) ?? { resortId, total: 0, open: 0, needsReview: 0, seriousOpen: 0 } : undefined;
  const issueTotals = issueSummary?.reduce(
    (a, s) => ({ open: a.open + s.open, review: a.review + s.needsReview, serious: a.serious + s.seriousOpen }),
    { open: 0, review: 0, serious: 0 }
  );
  // Podgląd otwartych pozycji: tylko dla ośrodków z otwartymi (bez liczników, np. starszy backend: dla wszystkich)
  const previewEnabled = (resortId: string) => (issueSummary ? (summaryFor(resortId)?.open ?? 0) > 0 : issueSummaryFailed);

  // Derived stats
  const allReports: Report[] = (data.resorts ?? []).flatMap((r: Resort) => r.reports ?? []);
  const statusCounts = useMemo(() => {
    const counts: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0 };
    allReports.forEach((r) => { counts[r.state] = (counts[r.state] ?? 0) + 1; });
    return counts;
  }, [allReports]);

  // Filtered resorts + reports
  const visibleResorts = useMemo(() => {
    return (data.resorts ?? [])
      .sort((a: Resort, b: Resort) => a.name < b.name ? -1 : 1)
      .map((r: Resort) => ({
        ...r,
        reports: (r.reports ?? []).filter((rep: Report) => {
          if (activeStatus !== null && rep.state !== activeStatus) return false;
          if (searchPhrase && !rep.name?.toLowerCase().includes(searchPhrase)) return false;
          return true;
        }).sort(byVisitNumberDesc),
      }));
  }, [data.resorts, activeStatus, searchPhrase]);

  return (
    <div className="flex h-full">

      {/* Left panel */}
      <aside className="w-[260px] bg-[var(--sc-surface)] border-r border-[var(--sc-border)] flex flex-col flex-shrink-0 overflow-hidden">

        {/* Stats */}
        <div className="px-5 py-5 border-b border-[var(--sc-border)]">
          <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-[var(--sc-muted)] mb-3.5">
            Podsumowanie
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <div className="bg-[var(--sc-bg)] rounded-[8px] px-3 py-2.5">
              <div className="text-[22px] font-light text-[var(--sc-text)] leading-none tracking-[-0.03em]">{allReports.length}</div>
              <div className="text-[10px] text-[var(--sc-muted)] mt-1 uppercase tracking-[0.05em]">Raporty</div>
            </div>
            <div className="bg-[var(--sc-bg)] rounded-[8px] px-3 py-2.5">
              <div className="text-[22px] font-light text-[var(--sc-text)] leading-none tracking-[-0.03em]">{(data.resorts ?? []).length}</div>
              <div className="text-[10px] text-[var(--sc-muted)] mt-1 uppercase tracking-[0.05em]">Ośrodki</div>
            </div>
            <div className="bg-[#FEF3E2] rounded-[8px] px-3 py-2.5">
              <div className="text-[22px] font-light text-[#C07810] leading-none tracking-[-0.03em]">{statusCounts[ReportState.INPROGRESS]}</div>
              <div className="text-[10px] text-[var(--sc-muted)] mt-1 uppercase tracking-[0.05em]">W trakcie</div>
            </div>
            <div className="bg-[var(--sc-bg)] rounded-[8px] px-3 py-2.5">
              <div className="text-[22px] font-light text-[var(--sc-text)] leading-none tracking-[-0.03em]">{statusCounts[ReportState.ACCEPTED]}</div>
              <div className="text-[10px] text-[var(--sc-muted)] mt-1 uppercase tracking-[0.05em]">Zaakceptowane</div>
            </div>
          </div>
        </div>

        {/* Rejestr badania: sumy ze wszystkich ośrodków, każda liczba otwiera rejestr badania z filtrem */}
        {studyId && (
          <div className="px-5 py-4 border-b border-[var(--sc-border)]">
            <div className="flex items-center mb-2.5">
              <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-[var(--sc-muted)]">Rejestr badania</span>
              <div className="flex-1" />
              <Link to={`/study/${studyId}/issues`} className="text-[11px] text-[var(--sc-blue)] hover:underline">
                Otwórz →
              </Link>
            </div>
            {issueTotals ? (
              <div className="flex flex-col gap-1">
                {[
                  { filter: "open", label: "Otwarte pozycje", n: issueTotals.open, alert: false },
                  { filter: "review", label: "Do weryfikacji", n: issueTotals.review, alert: issueTotals.review > 0 },
                  { filter: "seriousOpen", label: "Poważne otwarte", n: issueTotals.serious, alert: issueTotals.serious > 0 },
                ].map((t) => (
                  <Link
                    key={t.filter}
                    to={`/study/${studyId}/issues?filter=${t.filter}`}
                    className={cn(
                      "flex items-center justify-between rounded-[8px] px-3 py-1.5 no-underline transition-colors",
                      t.alert ? "bg-[var(--sc-red-bg)] hover:opacity-80" : "bg-[var(--sc-bg)] hover:bg-[var(--sc-amber-bg)]"
                    )}
                  >
                    <span className="text-[11px] text-[var(--sc-muted)]">{t.label}</span>
                    <span className={cn("text-[16px] font-light leading-none", t.alert ? "text-[var(--sc-red)]" : "text-[var(--sc-text)]")}>
                      {t.n}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-[var(--sc-muted)] m-0">Znaleziska i akcje ze wszystkich ośrodków.</p>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex-1 overflow-y-auto py-4">
          <span className="block text-[10px] font-medium uppercase tracking-[0.1em] text-[var(--sc-muted)] px-5 mb-2">
            Status
          </span>
          {STATUS_FILTERS.map(({ label, value }) => {
            const isActive = activeStatus === value;
            const count = value === null ? allReports.length : (statusCounts[value as number] ?? 0);
            return (
              <button
                key={label}
                onClick={() => setActiveStatus(value)}
                className={cn(
                  "w-full flex items-center gap-2.5 px-5 py-[7px] text-[12px] text-left border-none cursor-pointer transition-colors",
                  isActive ? "bg-[#FEF3E2] text-[var(--sc-text)] font-medium" : "bg-transparent text-[var(--sc-muted)] hover:bg-[var(--sc-bg)]"
                )}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{
                    background: isActive
                      ? "#F4A832"
                      : value !== null
                        ? STATUS_CONFIG[value as number]?.accent
                        : "#E8E8E6",
                  }}
                />
                {label}
                <span className="ml-auto text-[10px] text-[var(--sc-muted)]">{count}</span>
              </button>
            );
          })}

          <span className="block text-[10px] font-medium uppercase tracking-[0.1em] text-[var(--sc-muted)] px-5 mb-2 mt-4">
            Ośrodek
          </span>
          {(data.resorts ?? []).sort((a: Resort, b: Resort) => a.name < b.name ? -1 : 1).map((resort: Resort) => {
            const isActive = activeResortId === resort.id;
            const rCount = (resort.reports ?? []).length;
            const inProgress = (resort.reports ?? []).filter((r: Report) => r.state === ReportState.INPROGRESS).length;
            return (
              <button
                key={resort.id}
                onClick={() => {
                  if (isActive) {
                    setActiveResortId(null);
                  } else {
                    setActiveResortId(resort.id);
                    resortRefs.current[resort.id]?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }}
                className={cn(
                  "w-full flex flex-col gap-0.5 px-5 py-[9px] text-left cursor-pointer border-l-[3px] transition-colors border-none",
                  isActive
                    ? "bg-[var(--sc-bg)] border-l-[#5B8FD8]"
                    : "bg-transparent border-l-transparent hover:bg-[var(--sc-bg)]"
                )}
              >
                <span className="text-[12px] font-medium text-[var(--sc-text)]">{resort.name}</span>
                {resort.address && (
                  <span className="text-[10px] text-[var(--sc-muted)]">{resort.address}</span>
                )}
                <div className="flex flex-wrap gap-x-2 mt-1">
                  <span className="text-[10px] text-[var(--sc-muted)]">
                    <strong className="text-[var(--sc-text)] font-medium">{rCount}</strong> raportów
                  </span>
                  {inProgress > 0 && (
                    <span className="text-[10px] text-[var(--sc-muted)]">
                      <strong className="text-[var(--sc-text)] font-medium">{inProgress}</strong> w trakcie
                    </span>
                  )}
                  {(summaryFor(resort.id)?.open ?? 0) > 0 && (
                    <span className="text-[10px] text-[#C07810]">
                      <strong className="font-medium">{summaryFor(resort.id)!.open}</strong> otwartych
                    </span>
                  )}
                  {(summaryFor(resort.id)?.needsReview ?? 0) > 0 && (
                    <span className="text-[10px] text-[var(--sc-red)]">
                      <strong className="font-medium">{summaryFor(resort.id)!.needsReview}</strong> do weryf.
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Bottom actions */}
        <div className="px-3 py-3 border-t border-[var(--sc-border)] flex flex-col gap-2">
          <AddItemsModal
            text="Dodaj ośrodek"
            headers={["Nazwa", "Adres"]}
            saveItems={(names: string[]) => {
              addResortMutation({ name: names[0] ?? "", address: names[1] ?? "" });
            }}
          />
          <AddItemModal
            text="Edytuj opis badania"
            saveItem={(value) => updateDescription(value)}
            val={data.description}
          />
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto px-8 py-6">

        {/* Central Monitoring section */}
        {(centralMonitoringTemplate || cmReports.length > 0) && (
          <div className="mb-6 border border-[var(--sc-border)] rounded-[10px] overflow-hidden">
            {/* CM header */}
            <div className="bg-[var(--sc-bg)] px-4 py-3 flex items-center gap-3">
              <span className="text-[12px] font-semibold text-[var(--sc-text)] uppercase tracking-[0.08em]">Centralny Monitoring</span>
              <span className="text-[10px] text-[var(--sc-muted)] bg-[var(--sc-surface)] px-2 py-0.5 rounded-[8px] border border-[var(--sc-border)]">
                {cmReports.length} raportów
              </span>
              {centralMonitoringTemplate && (
                <a
                  href={`/templates/${centralMonitoringTemplate.id}`}
                  className="text-[11px] text-[var(--sc-muted)] underline hover:text-[#F4A832] ml-1"
                >
                  Edytuj szablon
                </a>
              )}
              <button
                onClick={() => createCMReport()}
                disabled={isCreatingCMReport}
                className="ml-auto rounded-full px-4 py-1.5 text-[12px] font-medium bg-[#F4A832] text-[#0F0F0F] hover:bg-[#e09620] transition-colors disabled:opacity-50 border-none cursor-pointer"
              >
                {isCreatingCMReport ? "Tworzenie..." : "+ Nowy raport CM"}
              </button>
            </div>
            {/* CM report list */}
            {cmReports.length === 0 ? (
              <p className="text-[12px] text-[var(--sc-muted)] px-4 py-3">Brak raportów centralnego monitoringu</p>
            ) : (
              <div className="divide-y divide-[#EBEBEA]">
                {cmReports.map((report: Report) => {
                  const cfg = STATUS_CONFIG[report.state];
                  return (
                    <div
                      key={report.id}
                      onClick={() => navigate(`/reports/${report.id}`)}
                      className="bg-[var(--sc-surface)] flex overflow-hidden cursor-pointer transition-all hover:bg-[var(--sc-bg)]"
                    >
                      <div className="w-1 flex-shrink-0" style={{ background: cfg?.accent ?? "#F4A832" }} />
                      <div className="flex-1 flex items-center gap-5 px-[18px] py-[12px]">
                        <div className="w-48 flex-shrink-0">
                          <div className="text-[13px] font-semibold text-[var(--sc-text)] tracking-[-0.01em]">{report.name}</div>
                          <div className="text-[11px] text-[var(--sc-muted)] mt-0.5">Centralny Monitoring</div>
                        </div>
                        <div className="flex-1 flex items-center gap-2">
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-semibold text-white flex-shrink-0"
                            style={{ background: "linear-gradient(135deg, #F4C87A, #D4921E)" }}
                          >
                            {report.createdBy?.slice(0, 2).toUpperCase()}
                          </div>
                          <span className="text-[11px] text-[var(--sc-muted)]">{report.createdBy}</span>
                          {report.created && (
                            <>
                              <span className="text-[#D0D0CE]">·</span>
                              <span className="text-[11px] text-[var(--sc-muted)]">{new Date(report.created).toLocaleDateString("pl")}</span>
                            </>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          {cfg && (
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[20px] text-[11px] font-medium ${cfg.pill}`}>
                              <span className="w-[5px] h-[5px] rounded-full" style={{ background: cfg.accent }} />
                              {cfg.label}
                            </span>
                          )}
                        </div>
                        <span className="text-[16px] text-[#D0D0CE] flex-shrink-0 ml-2">›</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center gap-2.5 mb-5">
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--sc-muted)]">
              <MagnifyingGlass />
            </span>
            <Input
              type="search"
              placeholder="Szukaj raportu..."
              className="pl-8 w-[220px]"
              onChange={onSearchChange}
            />
          </div>
          {STATUS_FILTERS.map(({ label, value }) => (
            <button
              key={label}
              onClick={() => setActiveStatus(value)}
              className={cn(
                "px-3 py-1 rounded-[20px] text-[11px] border cursor-pointer transition-colors",
                activeStatus === value
                  ? "bg-[var(--sc-text)] text-[var(--sc-surface)] border-[var(--sc-text)]"
                  : "bg-[var(--sc-surface)] text-[var(--sc-muted)] border-[var(--sc-border)] hover:border-[#C0C0BE]"
              )}
            >
              {label}
            </button>
          ))}
          <div className="flex-1" />
          <span className="text-[11px] text-[var(--sc-muted)]" title="Od najwyższego numeru wizyty; przy tym samym numerze najnowszy">Sortuj: numer wizyty ↓</span>
        </div>

        {/* Site groups */}
        {visibleResorts.map((resort: Resort) => (
          <div key={resort.id} className="mb-6" ref={(el) => { resortRefs.current[resort.id] = el; }}>
            {/* Site group header: wiersz 1 nazwa i adres (akcje po prawej), wiersz 2 liczba raportów i rejestr ośrodka */}
            <div className="mb-2.5 px-0.5 pb-2.5 border-b border-[var(--sc-border)]">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[var(--sc-text)] uppercase tracking-[0.08em] break-words">
                    {resort.name}
                  </div>
                  {resort.address && (
                    <div className="text-[11px] text-[var(--sc-muted)] mt-0.5">{resort.address}</div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {/* Edit/delete resort */}
                  <AddItemsModal
                    text="Edytuj ośrodek"
                    vals={[resort.name, resort.address ?? ""]}
                    headers={["Nazwa", "Adres"]}
                    saveItems={(names: string[]) => {
                      editResortMutation({ resortId: resort.id, name: names[0] ?? "", address: names[1] ?? "" });
                    }}
                  />
                  <button
                    className="p-1 rounded-md hover:bg-[var(--sc-bg)] text-[var(--sc-muted)] border-none bg-transparent cursor-pointer transition-colors"
                    onClick={async () => {
                      if (window.confirm("Czy chcesz usunąć ośrodek?")) await resortDelete(resort.id);
                    }}
                  >
                    <DeleteIcon />
                  </button>
                  <ReportDropdown
                    resortId={resort.id}
                    templates={(templates ?? []).filter((x: any) => x.studyId == studyId)}
                    addReportMutation={addReportMutation}
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="text-[10px] text-[var(--sc-muted)] bg-[var(--sc-bg)] px-2 py-0.5 rounded-[8px] whitespace-nowrap">
                  {resort.reports?.length ?? 0} raportów
                </span>
                <ResortRegisterButton resortId={resort.id} summary={summaryFor(resort.id)} />
              </div>
            </div>

            <ResortIssuesPreview resortId={resort.id} enabled={previewEnabled(resort.id)} />

            {/* Report cards */}
            {(resort.reports ?? []).length === 0 ? (
              <p className="text-[12px] text-[var(--sc-muted)] px-1 py-2">Brak raportów</p>
            ) : (
              resort.reports.map((report: Report) => {
                const cfg = STATUS_CONFIG[report.state];
                return (
                  <div
                    key={report.id}
                    onClick={() => navigate(`/reports/${report.id}`)}
                    className="bg-[var(--sc-surface)] border border-[var(--sc-border)] rounded-[10px] flex overflow-hidden mb-1.5 cursor-pointer transition-all hover:border-[var(--sc-muted)] hover:shadow-[0_2px_12px_rgba(0,0,0,0.05)]"
                  >
                    {/* Accent strip */}
                    <div className="w-1 flex-shrink-0" style={{ background: cfg?.accent ?? "#E8E8E6" }} />

                    {/* Body */}
                    <div className="flex-1 flex items-center gap-5 px-[18px] py-[14px]">
                      {/* Visit name */}
                      <div className="w-20 flex-shrink-0">
                        <div className="text-[14px] font-semibold text-[var(--sc-text)] tracking-[-0.01em]">
                          {report.name}
                        </div>
                        <div className="text-[11px] text-[var(--sc-muted)] mt-0.5">Raport</div>
                      </div>

                      {/* Meta */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-semibold text-white flex-shrink-0"
                            style={{ background: "linear-gradient(135deg, #99AECB, #6B8AAD)" }}
                          >
                            {report.createdBy?.slice(0, 2).toUpperCase()}
                          </div>
                          <span className="text-[11px] text-[var(--sc-muted)]">{report.createdBy}</span>
                          {report.created && (
                            <>
                              <span className="text-[#D0D0CE]">·</span>
                              <span className="text-[11px] text-[var(--sc-muted)]">
                                {formatDateTime(report.created)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="flex-shrink-0 text-right w-36">
                        {cfg && (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[20px] text-[11px] font-medium ${cfg.pill}`}>
                            <span className="w-[5px] h-[5px] rounded-full" style={{ background: cfg.accent }} />
                            {cfg.label}
                          </span>
                        )}
                      </div>

                      {/* Arrow */}
                      <span className="text-[16px] text-[#D0D0CE] flex-shrink-0 ml-2">›</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ))}
      </main>
    </div>
  );
};

export default Reports;
